import { io, type Socket } from 'socket.io-client';
import { ALERT_EVENT, type Alert } from '@curentia/shared';

const SESSION_ERRORS = new Set(['unauthenticated', 'invalid_token', 'session_ended']);

export interface AlertHandlers {
  onAlert(alert: Alert): void;
  onStatus(connected: boolean): void;
  /** Called when the server refused or dropped the session. Return true once signed in again. */
  onSessionLost(): Promise<boolean>;
}

/**
 * The live alert connection. The token travels in the handshake's auth field
 * and is read fresh on every attempt. Alerts carry IDs only.
 */
export class LiveAlerts {
  private socket: Socket | null = null;

  constructor(private readonly baseUrl: string) {}

  start(auth: () => { token: string | null; deviceId: string }, handlers: AlertHandlers): void {
    this.stop();
    const socket = io(this.baseUrl, {
      auth: (callback) => callback(auth()),
      reconnectionDelayMax: 30_000,
    });
    const resume = async () => {
      if (this.socket === socket && (await handlers.onSessionLost()) && this.socket === socket) socket.connect();
    };
    socket.on('connect', () => handlers.onStatus(true));
    socket.on('disconnect', (reason) => {
      handlers.onStatus(false);
      // The server drops a socket when its token expires; the client must come back itself.
      if (reason === 'io server disconnect') void resume();
    });
    socket.on('connect_error', (error) => {
      handlers.onStatus(false);
      if (SESSION_ERRORS.has(error.message)) void resume();
    });
    socket.on(ALERT_EVENT, (alert: Alert) => handlers.onAlert(alert));
    this.socket = socket;
  }

  stop(): void {
    const socket = this.socket;
    this.socket = null;
    socket?.removeAllListeners();
    socket?.disconnect();
  }
}
